import React, { useState, useEffect } from 'react';
import { X, Download, Eye, Calendar, DollarSign, CheckCircle, AlertCircle, FileText, Clock } from 'lucide-react';
import ClientAuthService from '../services/clientAuthService';
import Modal from './ui/Modal';

const ClientInvoiceModal = ({ invoiceId, onClose }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvoice();
  }, [invoiceId]);

  const loadInvoice = async () => {
    try {
      setLoading(true);
      const response = await ClientAuthService.getInvoice(invoiceId);
      console.log('Fetched invoice:', response.data);
      setInvoice(response.data);
    } catch (error) {
      setError('Failed to load invoice: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      await ClientAuthService.downloadInvoice(invoiceId);
    } catch (error) {
      alert('Failed to download invoice: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'paid': { class: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Paid' },
      'sent': { class: 'bg-yellow-100 text-yellow-800', icon: Eye, text: 'Sent' },
      'overdue': { class: 'bg-red-100 text-red-800', icon: AlertCircle, text: 'Overdue' },
      'partially-paid': { class: 'bg-blue-100 text-blue-800', icon: DollarSign, text: 'Partially Paid' },
      'payment-pending': { class: 'bg-blue-100 text-blue-800', icon: Clock, text: 'Payment Processing' }
    };

    const config = statusConfig[status] || { class: 'bg-gray-100 text-gray-800', icon: AlertCircle, text: status };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.class}`}>
        <Icon size={12} className="mr-1" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Loading Invoice"
        size="large"
      >
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invoice...</p>
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Error"
        size="large"
        footer={
          <button 
            type="button" 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" 
            onClick={onClose}
          >
            Close
          </button>
        }
      >
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        <>
          <FileText className="mr-2" size={20} />
          Invoice {invoice?.number}
        </>
      }
      size="large"
      footer={
        <div className="flex justify-end space-x-3">
          <button 
            type="button" 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" 
            onClick={onClose}
          >
            Close
          </button>
          <button 
            type="button" 
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" 
            onClick={handleDownload}
          >
            <Download size={16} className="mr-2" />
            Download PDF
          </button>
        </div>
      }
    >
      <div>
        {invoice && (
          <div>
            {/* Invoice Header */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h6 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Invoice Details</h6>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-900">Invoice Number:</span>
                    <span className="ml-2 text-gray-700">{invoice.number}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Date:</span>
                    <span className="ml-2 text-gray-700">{formatDate(invoice.date)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Due Date:</span>
                    <span className="ml-2 text-gray-700">{formatDate(invoice.dueDate)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Status:</span>
                    <span className="ml-2">{getStatusBadge(invoice.status)}</span>
                  </div>
                  {invoice.status === 'payment-pending' && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-start">
                      <Clock size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                      <small>
                        Payment is being processed by GoCardless. This typically takes 1-3 business days to complete.
                      </small>
                    </div>
                  )}
                  {invoice.po_number && (
                    <div>
                      <span className="font-medium text-gray-900">PO Number:</span>
                      <span className="ml-2 text-gray-700">{invoice.po_number}</span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h6 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Amount Summary</h6>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Total Amount:</span>
                      <span className="font-semibold text-gray-900">{formatCurrency(invoice.totalAmount)}</span>
                    </div>
                    {invoice.total_paid > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Paid:</span>
                        <span className="font-medium text-green-600">{formatCurrency(invoice.totalPaid)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Amount Due:</span>
                      <span className={`font-semibold ${invoice.amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(invoice.amountDue)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Items */}
            <div className="mb-6">
              <h6 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Invoice Items</h6>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoice.items?.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-4">
                          <div className="font-medium text-gray-900">{item.itemName}</div>
                          {item.description && (
                            <div className="text-sm text-gray-500">{item.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">{item.quantity}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{formatCurrency(item.price)}</td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">{formatCurrency(item.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Invoice Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-full md:w-1/2">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Subtotal:</span>
                      <span className="text-gray-900">{formatCurrency(invoice.subtotal)}</span>
                    </div>
                    {invoice.discount_amount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Discount ({invoice.discountPercentage}%):</span>
                        <span className="text-green-600">-{formatCurrency(invoice.discountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Tax:</span>
                      <span className="text-gray-900">{formatCurrency(invoice.taxAmount)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-900">Total:</span>
                        <span className="font-bold text-lg text-gray-900">{formatCurrency(invoice.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div>
                <h6 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Notes</h6>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{invoice.notes}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ClientInvoiceModal;